/**
 * VestuarioVirtual — AR-style virtual locker room
 * Mock visual estilo mockup + botón WebXR para AR real cuando el navegador lo soporte.
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Cube, Sparkle, Lightning, ArrowsClockwise, Camera, ShoppingBag, Robot, ArrowLeft } from "@phosphor-icons/react";
import HumanoidAvatar from "@/components/HumanoidAvatar";
import { toast } from "sonner";

const KITS = [
  { id: "pro_neon",   name: "Pro Neon",        pala: "Bullpadel",  apparel: "Adidas",   shoes: "Asics",   color: "#CCFF00" },
  { id: "elite_black", name: "Elite Black",     pala: "Babolat",    apparel: "Nike",     shoes: "Wilson",  color: "#FF6B00" },
  { id: "club_classic", name: "Club Classic",   pala: "Head",       apparel: "Lacoste",  shoes: "Joma",    color: "#34C759" },
  { id: "torneo_gold", name: "Torneo Gold",     pala: "Wilson",     apparel: "Puma",     shoes: "Mizuno",  color: "#FFD700" },
];

const ACCESORIES = ["Gorra Pro", "Muñequera DYD", "Banda LED", "Lentes UV"];

export default function VestuarioVirtual() {
  const { user } = useAuth();
  const [activeKit, setActiveKit] = useState(KITS[0]);
  const [aiMessage, setAiMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [xrSupported, setXrSupported] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const avatarRef = useRef(null);

  // Detect WebXR support
  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.xr) {
      navigator.xr.isSessionSupported("immersive-ar").then((ok) => setXrSupported(!!ok)).catch(() => setXrSupported(false));
    }
  }, []);

  const askDYD = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/dyd/chat", {
        message: `Soy ${user?.name || "un jugador"}. Estoy probándome el kit "${activeKit.name}" (pala ${activeKit.pala}, ropa ${activeKit.apparel}, calzado ${activeKit.shoes}). Dame en 2-3 oraciones por qué este kit me favorece o sugiéreme alguno mejor para mi estilo. Tono motivador.`,
      });
      setAiMessage(data?.reply || data?.message || "Estás listo para entrar a la cancha con autoridad.");
    } catch {
      setAiMessage("Estás listo para entrar a la cancha con autoridad. Confía en tu equipo.");
    } finally {
      setAiLoading(false);
    }
  };

  const launchAR = async () => {
    if (!xrSupported) {
      toast.info("AR no soportado en este navegador. Abre desde Chrome Android o Safari iOS para WebXR.");
      return;
    }
    try {
      const session = await navigator.xr.requestSession("immersive-ar", { requiredFeatures: ["hit-test"] });
      toast.success("Sesión AR iniciada. Apunta la cámara a la cancha.");
      session.addEventListener("end", () => toast("Sesión AR finalizada"));
    } catch {
      toast.error("No se pudo iniciar la sesión AR");
    }
  };

  const triggerSpin = () => {
    setSpinning(true);
    setTimeout(() => setSpinning(false), 1200);
  };

  // Apply selected kit gear to a virtual user
  const previewUser = {
    ...user,
    avatar_style: "personas",
    gear_pala_brand: activeKit.pala.toLowerCase(),
    gear_apparel_brand: activeKit.apparel.toLowerCase(),
    gear_shoes_brand: activeKit.shoes.toLowerCase(),
    gear_accessories: ACCESORIES[0],
  };

  return (
    <div className="space-y-6 relative" data-testid="vestuario-virtual-page">
      {/* Neon background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,160,255,0.18) 0%, transparent 70%)" }} />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: `radial-gradient(circle, ${activeKit.color}22 0%, transparent 70%)` }} />
      </div>

      <div className="relative flex items-center justify-between gap-2 flex-wrap">
        <Link to="/dashboard" className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-[#CCFF00] flex items-center gap-1 font-semibold" data-testid="vestuario-back">
          <ArrowLeft size={12} /> Volver al inicio
        </Link>
        <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] tracking-widest">
          <Lightning size={11} className="mr-1" weight="fill" /> {xrSupported ? "WebXR disponible" : "WebXR no disponible"}
        </Badge>
      </div>

      {/* HERO */}
      <div className="relative card-tactical overflow-hidden" data-testid="vestuario-hero" style={{ borderColor: `${activeKit.color}66` }}>
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute top-4 right-4 w-20 h-20 opacity-50" viewBox="0 0 100 100" fill="none">
            <path d="M30 10 L25 50 L45 50 L40 90 L70 40 L50 40 L55 10 Z" stroke={activeKit.color} strokeWidth="2" fill={`${activeKit.color}22`} />
          </svg>
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-3/4 h-24 rounded-full blur-3xl opacity-40" style={{ background: activeKit.color }} />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 p-6 sm:p-8">
          <div className="flex flex-col gap-3">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: activeKit.color }}>Tu Vestuario Virtual</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none">
              Personalización<br />con Inteligencia AR
            </h1>
            <p className="text-sm text-gray-300 max-w-md">Prueba un nuevo kit, gírate 360° y véte en realidad aumentada antes de entrar a la cancha. DYD analiza tu estilo y recomienda.</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button onClick={triggerSpin} className="font-bold uppercase text-xs h-10 px-4" style={{ backgroundColor: activeKit.color, color: "#000" }} data-testid="vestuario-spin">
                <ArrowsClockwise size={14} className="mr-1" weight="bold" /> Giro 360°
              </Button>
              <Button onClick={launchAR} className="bg-blue-500 text-white hover:bg-blue-600 font-bold uppercase text-xs h-10 px-4" data-testid="vestuario-ar-launch">
                <Camera size={14} className="mr-1" weight="duotone" /> Ver en mi Cancha (AR)
              </Button>
              <Button onClick={askDYD} disabled={aiLoading} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase text-xs h-10 px-4" data-testid="vestuario-ask-dyd">
                <Robot size={14} className="mr-1" weight="duotone" /> {aiLoading ? "Analizando…" : "Consultar a DYD"}
              </Button>
            </div>
            {aiMessage && (
              <div className="mt-2 p-3 bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] text-xs" data-testid="vestuario-ai-message">
                <Sparkle size={12} weight="fill" className="inline mr-1" /> {aiMessage}
              </div>
            )}
          </div>

          {/* Avatar with lightning */}
          <div className="flex flex-col items-center justify-center relative">
            <div className={`relative ${spinning ? "animate-spin" : ""}`} style={{ transition: "transform 1.2s" }} ref={avatarRef}>
              <svg className="absolute -inset-6 w-[calc(100%+48px)] h-[calc(100%+48px)] pointer-events-none" viewBox="0 0 240 240" fill="none">
                <path d="M120 0 L100 80 L160 80 L120 160 L120 240" stroke={activeKit.color} strokeWidth="1.5" opacity="0.6" />
                <path d="M0 100 L80 120 L160 100 L240 130" stroke="#00A0FF" strokeWidth="1" opacity="0.5" strokeDasharray="6 4" />
              </svg>
              <HumanoidAvatar user={previewUser} size={220} testId="vestuario-avatar" />
            </div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-4">Kit activo: <span style={{ color: activeKit.color }}>{activeKit.name}</span></p>
          </div>
        </div>
      </div>

      {/* KIT SHOP */}
      <div className="card-tactical p-5" data-testid="vestuario-kits">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag size={18} weight="duotone" className="text-[#CCFF00]" />
          <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Probar Nuevo Kit</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {KITS.map((k) => {
            const on = activeKit.id === k.id;
            return (
              <button
                key={k.id}
                type="button"
                onClick={() => setActiveKit(k)}
                className={`text-left p-4 border transition-all ${on ? "ring-2" : "opacity-80 hover:opacity-100"}`}
                style={{
                  borderColor: on ? k.color : "rgba(255,255,255,0.1)",
                  backgroundColor: on ? `${k.color}10` : "#0A0A0A",
                  boxShadow: on ? `0 0 0 1px ${k.color}` : undefined,
                }}
                data-testid={`vestuario-kit-${k.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Cube size={20} weight="duotone" style={{ color: k.color }} />
                  <p className="font-['Barlow_Condensed'] font-black text-lg uppercase" style={{ color: on ? k.color : "#FFF" }}>{k.name}</p>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 space-y-0.5">
                  <p>Pala: <span className="text-white">{k.pala}</span></p>
                  <p>Ropa: <span className="text-white">{k.apparel}</span></p>
                  <p>Calzado: <span className="text-white">{k.shoes}</span></p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Manager AR Hint */}
      <div className="card-tactical p-5 flex items-start gap-3" data-testid="vestuario-manager">
        <Robot size={32} weight="duotone" className="text-[#CCFF00] shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] mb-0.5">DYD Gestor AR</p>
          <h3 className="font-['Barlow_Condensed'] text-xl font-black uppercase mb-1">Gestión inteligente del equipamiento</h3>
          <p className="text-xs text-gray-400">
            Tu coach DYD analiza marca, peso, comportamiento de la pala y tu historial de partidos para recomendar el kit ideal en cada cancha. Toca <strong className="text-[#CCFF00]">Consultar a DYD</strong> arriba para recibir un análisis personalizado.
          </p>
          <p className="text-[10px] text-blue-300 mt-2">
            <strong>AR Tip:</strong> en móviles compatibles, el botón <em>Ver en mi Cancha</em> abre una sesión WebXR con detección de superficie para superponer tu avatar en la cancha real.
          </p>
        </div>
      </div>
    </div>
  );
}
